import React, { useState, useRef, useEffect } from 'react';
import { getImageUrl } from '../utils/imageHelpers';

interface ImageUploadProps {
  existingImage?: string; // Single image filename or full URL from backend
  onImageChange: (file: File | null) => void;
  onRemoveExisting?: () => void;
  accept?: string;
  className?: string;
  label?: string;
  name?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  existingImage,
  onImageChange,
  onRemoveExisting,
  accept = '.png, .jpg, .jpeg',
  className = '',
  label = 'Upload Picture',
  name = 'picture',
}) => {
  const [preview, setPreview] = useState<string>('');
  const [hasImage, setHasImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set initial preview from existing image
  useEffect(() => {
    if (existingImage) {
      const imageUrl = getImageUrl(existingImage);
      setPreview(imageUrl);
      setHasImage(true);
    } else {
      setPreview('');
      setHasImage(false);
    }
  }, [existingImage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      setHasImage(true);
      onImageChange(file);
    }
  };

  const handleCancel = () => {
    // Reset to existing image or empty
    if (existingImage) {
      const imageUrl = getImageUrl(existingImage);
      setPreview(imageUrl);
      setHasImage(true);
      onImageChange(null);
    } else {
      setPreview('');
      setHasImage(false);
      onImageChange(null);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setPreview('');
    setHasImage(false);
    onImageChange(null);
    if (onRemoveExisting) {
      onRemoveExisting();
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  return (
    <div className={className}>
      <label className="fs-6 fw-semibold mb-3">
        <span>{label}</span>
        <i className="fas fa-exclamation-circle ms-1 fs-7" title="Allowed file types: png, jpg, jpeg."></i>
      </label>
      <div className="mt-1">
        <div className={`image-input image-input-outline image-input-placeholder ${!hasImage ? 'image-input-empty' : ''}`} data-kt-image-input="true">
          {/* Preview */}
          <div 
            className="image-input-wrapper w-100px h-100px" 
            style={{ 
              backgroundImage: preview ? `url(${preview})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          
          {/* Change button */}
          <label 
            className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" 
            data-kt-image-input-action="change" 
            title="Change avatar"
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-pencil-alt fs-7"></i>
            <input
              ref={fileInputRef}
              type="file"
              name={name}
              accept={accept}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </label>
          
          {/* Cancel button */}
          <span 
            className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" 
            data-kt-image-input-action="cancel" 
            title="Cancel avatar"
            onClick={handleCancel}
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-times fs-2"></i>
          </span>
          
          {/* Remove button */}
          <span 
            className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" 
            data-kt-image-input-action="remove" 
            title="Remove avatar"
            onClick={handleRemove}
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-times fs-6 p-1"></i>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;

